import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PageNavigation from "@/components/PageNavigation";
import SEOHead from "@/components/seo/SEOHead";
import juniorBianchim from "@/assets/junior-bianchim.jpg";
import postCoverNegociacao from "@/assets/cover-negociacao-b2b.jpg";
import postReuniao from "@/assets/mentoria-3.jpg";
import postHandshake from "@/assets/mentoria-6.jpg";
import postCoverSocialSelling from "@/assets/cover-social-selling.jpg";
import postCoverPreVendas from "@/assets/cover-pre-vendas.jpg";
import postCoverEstrategias from "@/assets/cover-13-estrategias.jpg";
import {
  ArrowRight,
  Clock,
  User,
  BookOpen,
  CheckCircle,
  ChevronRight,
  Target,
  Zap,
  Users,
  MessageCircle,
  BarChart3,
  Star,
  AlertTriangle,
  Lightbulb,
  Award,
  Search,
  Calendar,
  TrendingUp,
  Briefcase,
  HandCoins,
  Shield,
  Handshake,
  Scale,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

const NegociacaoB2B2026 = () => {
  const navigate = useNavigate();

  const checklist = [
    {
      number: "01",
      title: "Pare de Negociar Preço — Negocie Valor Percebido",
      icon: Scale,
      problem: "Toda reunião acaba em desconto porque o cliente só fala em preço?",
      action:
        "Quando você entra numa negociação sem ter ancorado valor antes, a única variável que sobra é desconto. Em 2026, o comprador B2B está treinado em frameworks de procurement (Kraljic, TCO, RFP), e seu time de compras tem KPI de redução de custo. Se você não traz números de impacto antes da proposta — receita gerada, custo evitado, payback em meses — vira commodity. Toda negociação madura começa muito antes da reunião final: começa nas perguntas SPIN da descoberta, na construção da dor financeira e na quantificação do problema. Sem isso, qualquer desconto que você der vai ser pequeno demais para o cliente e grande demais para sua margem.",
      checkItems: [
        "Quantifique a dor em R$ antes de apresentar preço (receita perdida, custo, tempo)",
        "Apresente ROI estimado em cada proposta com 3 cenários (conservador, realista, otimista)",
        "Use a fórmula: investimento / retorno mensal = payback em meses",
        "Substitua 'preço' por 'investimento' em 100% das comunicações escritas e faladas",
        "Treine o time a responder 'está caro' com pergunta, nunca com desconto",
      ],
      story: null,
    },
    {
      number: "02",
      title: "Domine BATNA e ZOPA Antes de Sentar à Mesa",
      icon: Target,
      problem: "Você entra na negociação sem saber até onde pode ceder nem qual é o plano B?",
      action:
        "BATNA (Best Alternative to a Negotiated Agreement) é o que você faz se o acordo não sair. ZOPA (Zone of Possible Agreement) é a faixa onde existe acordo viável entre você e o cliente. Sem esses dois números mapeados, você negocia no escuro: ou aceita qualquer coisa por medo de perder, ou perde negócios que estavam fechados por exigir demais. Em 2026 isso é básico para fechar contratos acima de R$ 100 mil. Antes de qualquer reunião final, defina: piso (mínimo aceitável para fechar com margem saudável), alvo (proposta ideal) e teto (ponto onde levantar e ir embora). Sem essa preparação, você é refém do humor do comprador.",
      checkItems: [
        "Liste sua BATNA por escrito antes de cada negociação relevante",
        "Defina piso, alvo e teto numéricos para cada proposta",
        "Mapeie a BATNA percebida do cliente (quais alternativas ele tem no mercado)",
        "Crie um one-pager interno com ZOPA aprovada pelo gestor antes da reunião",
      ],
      story: null,
    },
    {
      number: "03",
      title: "Use Ancoragem Estratégica — Quem Fala Primeiro, Vence",
      icon: Lightbulb,
      problem: "Você espera o cliente dizer o orçamento dele antes de apresentar valor?",
      action:
        "A literatura comportamental de Kahneman é clara: a primeira âncora numérica define todo o restante da negociação. Quem espera o cliente falar primeiro está entregando o jogo. Em 2026, com IA ajudando compradores a preparar contrapropostas, ancoragem virou habilidade defensiva também. Apresente o investimento mais alto primeiro — pacote premium completo —, com justificativa de valor robusta. O comprador vai instintivamente comparar tudo a esse número. Mesmo que ele negocie para baixo, fechará num patamar muito superior ao que fecharia se você tivesse perguntado quanto ele tem.",
      checkItems: [
        "Comece sempre apresentando o pacote mais completo (ancoragem alta)",
        "Mostre 3 opções: premium, ideal e essencial — nessa ordem visual",
        "Justifique cada faixa de preço com entregáveis e impacto financeiro",
        "Nunca pergunte 'quanto vocês têm de orçamento?' — pergunte 'qual o impacto financeiro do problema?'",
      ],
      story: null,
    },
    {
      number: "04",
      title: "Faça Concessões Com Contrapartida — Nunca de Graça",
      icon: HandCoins,
      problem: "Você dá desconto só para mostrar boa vontade ou avançar a venda?",
      action:
        "Toda concessão sem contrapartida desvaloriza o produto e treina o cliente a pedir mais. Regra de ouro em 2026: para cada centavo cedido, peça algo em troca. Pode ser prazo de contrato mais longo, pagamento à vista, case de sucesso, indicação de outras unidades, expansão de escopo, antecipação de assinatura. Concessão estratégica preserva margem, valida valor e amarra commitment. Concessão desesperada destrói a percepção de preço e contamina renovações futuras — o cliente vai sempre lembrar que conseguiu 20% de desconto e exigir o mesmo no próximo ciclo.",
      checkItems: [
        "Crie uma tabela interna de concessões e contrapartidas equivalentes",
        "Nunca dê desconto sem pedir algo em troca por escrito",
        "Documente cada concessão e contrapartida na proposta final",
        "Treine o time a usar a frase: 'Posso analisar essa condição se vocês...'",
        "Evite descontos lineares — prefira bônus, brindes ou módulos extras",
      ],
      story: null,
    },
    {
      number: "05",
      title: "Aprenda a Lidar Com o Silêncio — Ele é Seu Aliado",
      icon: MessageCircle,
      problem: "Logo após apresentar o preço você começa a justificar, explicar e dar descontos sozinho?",
      action:
        "O vendedor amador odeia silêncio. O profissional adora. Depois de apresentar a proposta, calar é a técnica mais poderosa de negociação que existe. O cliente precisa de tempo para processar, comparar com a dor, fazer contas mentais. Se você fala antes dele, está rebaixando seu próprio valor e abrindo desconto sem ele pedir. Em 2026, com reuniões cada vez mais por vídeo, o silêncio fica ainda mais desconfortável — e justamente por isso, mais poderoso. Conte mentalmente até 10 antes de dizer qualquer coisa após apresentar o investimento.",
      checkItems: [
        "Conte até 10 mentalmente depois de apresentar o preço",
        "Nunca seja você o primeiro a quebrar o silêncio na faixa de preço",
        "Pratique pausas estratégicas em simulações com o time",
        "Trate objeções com pergunta de volta, nunca com justificativa imediata",
      ],
      story: null,
    },
    {
      number: "06",
      title: "Mapeie Todos os Decisores e Influenciadores Antes da Proposta",
      icon: Users,
      problem: "Você apresentou proposta linda e o cliente sumiu porque 'precisa falar com a diretoria'?",
      action:
        "Em 2026, a venda B2B média envolve 7 a 11 decisores (Gartner). Negociar com apenas um deles é receita garantida para perder o negócio na última etapa. Antes da proposta final, mapeie: decisor econômico (quem assina), decisor técnico (quem valida), usuário final (quem vai operar), influenciador (quem tem voz na decisão), bloqueador (quem pode dizer não), patrocinador interno (quem te ajuda por dentro). Cada perfil tem dor diferente — e sua proposta precisa endereçar todas elas. CHAMP, MEDDIC e Command of the Message são frameworks que cobrem isso bem.",
      checkItems: [
        "Use um mapa de stakeholders por negócio com nome, cargo, papel e influência",
        "Identifique 1 patrocinador interno que vai defender você na ausência",
        "Adapte mensagens para cada perfil: técnico ouve detalhe, CFO ouve número, CEO ouve estratégia",
        "Não envie proposta antes de validar quem precisa aprovar",
        "Pergunte: 'além de você, quem mais participa dessa decisão?'",
      ],
      story: null,
    },
    {
      number: "07",
      title: "Domine a Arte de Dizer Não — Sem Perder o Negócio",
      icon: Shield,
      problem: "Você aceita qualquer condição para não 'estressar' o cliente e fechar de qualquer jeito?",
      action:
        "Saber dizer não é uma das competências mais escassas em vendas. Vendedor que diz sim para tudo perde respeito, margem e autoridade. Em negociação madura, o 'não' é um sinal de profissionalismo: significa que existe critério, padrão e proteção do cliente também. Quando você cede em tudo, o cliente desconfia: 'se ele dá esse desconto pra mim, dá pra qualquer um — então o preço original era inflado'. Diga não com técnica: explique o motivo, ofereça alternativa e mantenha a porta aberta. Em 2026, comprador respeita vendedor com posicionamento claro — e desconfia de quem cede demais.",
      checkItems: [
        "Crie uma lista de 5 condições inegociáveis em qualquer contrato",
        "Treine respostas como: 'Não consigo essa condição, mas posso oferecer X'",
        "Use a fórmula: motivo + alternativa + manutenção do vínculo",
        "Evite 'deixa eu ver com o chefe' como resposta padrão — gera percepção de falta de autonomia",
      ],
      story: null,
    },
    {
      number: "08",
      title: "Conduza Reunião Final Com Agenda Por Escrito",
      icon: Calendar,
      problem: "Suas reuniões de fechamento viram conversa solta e terminam sem decisão?",
      action:
        "Reunião de fechamento sem agenda é loteria. Em 2026, comprador profissional respeita vendedor que conduz reunião com método. Envie a pauta por escrito 24h antes, valide o tempo disponível, defina o objetivo (decisão de avanço) e os próximos passos esperados. No início, faça a 'mútua agenda check': 'nosso objetivo hoje é definir contrato e cronograma de implementação — vocês concordam?'. Se o cliente não está pronto para decidir, descubra antes de gastar 60 minutos. Reunião sem objetivo declarado é reunião perdida.",
      checkItems: [
        "Envie agenda escrita por e-mail 24h antes de toda reunião de fechamento",
        "Valide o objetivo da reunião nos primeiros 2 minutos",
        "Defina o próximo passo concreto antes de encerrar (data, responsável, entregável)",
        "Resuma decisões por e-mail em até 1h após a reunião",
        "Use um template de ata padronizada do time comercial",
      ],
      story: null,
    },
    {
      number: "09",
      title: "Use Gatilhos de Urgência Reais — Nunca Fabricados",
      icon: Zap,
      problem: "Você inventa promoção de última hora para forçar fechamento?",
      action:
        "Urgência fabricada queima credibilidade — e comprador B2B em 2026 reconhece de longe. Mas urgência real (final de trimestre, mudança regulatória, capacidade limitada de implementação, reajuste contratual em data específica) é arma legítima e poderosa. A diferença é simples: urgência verdadeira gera ação; urgência falsa gera desconfiança. Antes de usar gatilho de prazo, garanta que ele é real, documentado e justificável. E sempre alinhado ao benefício do cliente, não à sua meta do mês.",
      checkItems: [
        "Liste 3 gatilhos reais de urgência da sua operação (capacidade, reajuste, prazo regulatório)",
        "Nunca invente promoção fake para forçar fechamento",
        "Documente o motivo da urgência na proposta por escrito",
        "Mostre o custo da inação em R$/mês — esse é o melhor gatilho de urgência",
      ],
      story: null,
    },
    {
      number: "10",
      title: "Negocie Termos Contratuais Como Negocia Preço",
      icon: Briefcase,
      problem: "Você foca tanto em fechar valor que esquece de proteger reajuste, escopo e cláusulas?",
      action:
        "Margem real não está só no preço de venda — está nos termos. Reajuste anual indexado, multa de cancelamento, exclusividade, escopo bem delimitado, prazo de pagamento, garantia, foro: tudo isso impacta lucratividade e previsibilidade tanto quanto o ticket. Em 2026, com inflação volátil e contratos plurianuais voltando ao mercado B2B, deixar termo aberto é abrir buraco na conta. Negocie cada cláusula com a mesma seriedade do valor. E sempre proponha o contrato — quem propõe a redação, dita as condições. Aceitar contrato do cliente é abrir mão da metade da disputa.",
      checkItems: [
        "Tenha um contrato-padrão revisado por jurídico com cláusulas pró-empresa",
        "Inclua reajuste anual indexado (IPCA, IGP-M ou índice setorial) em todo contrato",
        "Defina escopo com entregáveis numerados e exclusões explícitas",
        "Proponha sempre o contrato — não aceite começar pelo do cliente",
        "Tenha 2 a 3 cláusulas 'de troca' que podem ser cedidas para fechar",
      ],
      story: null,
    },
    {
      number: "11",
      title: "Use Vídeo, Demo e Prova Social na Negociação — Não Só no Início",
      icon: Award,
      problem: "Você usa cases e demos só na descoberta e abandona prova social na negociação?",
      action:
        "Prova social é arma de negociação, não só de descoberta. Na hora do fechamento, quando o cliente hesita, um depoimento em vídeo de um cliente parecido com ele resolve mais que qualquer planilha. Em 2026, com Loom, Vidyard e WhatsApp Business permitindo envio rápido de vídeo personalizado, o vendedor que usa prova social na fase final tem 35% mais taxa de fechamento (HubSpot Research). Mande vídeo curto do CEO atual cliente falando do resultado, antes de fechar grande conta. Custa nada e converte tudo.",
      checkItems: [
        "Mantenha biblioteca de vídeos curtos de clientes por segmento e cargo",
        "Use Loom para enviar pitch personalizado pós-reunião",
        "Compartilhe case com números reais (receita, redução de custo, prazo) no momento da hesitação",
        "Peça depoimento sempre após renovação ou case de sucesso",
        "Treine o time a usar prova social como contraprova de objeção",
      ],
      story: null,
    },
    {
      number: "12",
      title: "Saiba a Hora de Levantar e Ir Embora",
      icon: Handshake,
      problem: "Você fica refém de cliente que quer 60% de desconto e empurra prazo eternamente?",
      action:
        "Vendedor que não sabe sair da mesa não negocia: pede esmola. Walk-away power é a maior alavanca de negociação que existe. Quando o cliente percebe que você precisa daquela venda a qualquer custo, ele esmaga você. Quando percebe que você tem alternativas e padrão mínimo, ele respeita e fecha. Em 2026, com pipeline robusto e diversificado, ir embora de negócio ruim deveria ser rotina, não exceção. Defina antes da reunião o teto que justifica encerrar conversa — e cumpra. Negócio ruim trava capacidade, queima margem e contamina o time. Diga obrigado, encerre com elegância e direcione energia para o próximo lead.",
      checkItems: [
        "Defina o teto inegociável por escrito antes da reunião",
        "Pratique a frase: 'Pelo que vejo, talvez não seja o melhor momento — fico à disposição'",
        "Acompanhe taxa de win/loss e proporção de descontos por vendedor",
        "Mantenha pipeline 4x acima da meta para ter walk-away power real",
        "Celebre internamente quando o time recusa negócio ruim — gera cultura saudável",
      ],
      story: null,
    },
    {
      number: "13",
      title: "Treine Negociação Em Simulação — Toda Semana, Sem Exceção",
      icon: TrendingUp,
      problem: "Seu time aprende a negociar no campo, errando com cliente real e queimando oportunidades?",
      action:
        "Negociação é habilidade muscular: só desenvolve com repetição em ambiente controlado. Pilotos treinam em simulador antes de voar. Cirurgiões praticam em manequins antes de operar. Vendedores deveriam praticar em simulações antes de cada negociação relevante — mas a maioria aprende no cliente real, errando, perdendo deal e culpando o mercado. Em 2026, com IA capaz de simular comprador realista, role-play virou commodity. Quem faz, fecha mais. Quem não faz, perde de quem faz. O Vendas Trainer foi construído exatamente para isso: simulações de IA com cenários reais, feedback instantâneo, métricas individuais. Quem treina semanalmente em ambiente controlado entra na reunião do cliente com cinco rodadas de prática já feitas.",
      checkItems: [
        "Implemente sessão semanal de role-play obrigatória no time comercial",
        "Use IA (Vendas Trainer) para simulações entre as sessões presenciais",
        "Grave reuniões reais e revise com o time em mentoria coletiva quinzenal",
        "Crie biblioteca de objeções recorrentes e respostas validadas",
        "Meça evolução individual em cada simulação com score de critérios objetivos",
      ],
      story: `Em times que treinam negociação semanalmente com simulação de IA do Vendas Trainer, a taxa de fechamento cresce em média 31% nos primeiros 90 dias e o desconto médio cai 18%. Não é mágica — é repetição com feedback. O cérebro responde a treino igual músculo responde a peso.`,
    },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Negociação B2B em 2026: Como Conduzir Reuniões Que Fecham Grandes Contratos",
    description:
      "Guia completo de negociação B2B em 2026 em 13 passos: BATNA, ZOPA, ancoragem, concessões, stakeholders, contratos, walk-away power e treino com IA.",
    author: {
      "@type": "Person",
      name: "Junior Bianchim",
      jobTitle: "Especialista em Treinamento de Equipes Comerciais",
    },
    publisher: { "@type": "Organization", name: "Bianchim Business" },
    datePublished: "2026-05-05",
    dateModified: "2026-05-05",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://bianchimbusiness.com.br/posts/negociacao-b2b-2026",
    },
    image: "https://bianchimbusiness.com.br/og-negociacao-b2b-2026.jpg",
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "O que é BATNA em negociação B2B?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "BATNA (Best Alternative to a Negotiated Agreement) é a melhor alternativa que você tem caso o acordo não saia. Conhecer sua BATNA antes da reunião protege a margem, dá clareza de até onde ceder e evita que você feche negócios ruins por medo de perder o deal.",
        },
      },
      {
        "@type": "Question",
        name: "Como negociar sem dar desconto?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Quantifique a dor do cliente em R$ antes de apresentar preço, apresente ROI com três cenários, ancore alto começando pelo pacote premium e só conceda mediante contrapartida (prazo, escopo, indicação). Substitua desconto por bônus ou módulos extras sempre que possível.",
        },
      },
      {
        "@type": "Question",
        name: "Quantos decisores existem em uma venda B2B em 2026?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Segundo o Gartner, a venda B2B média em 2026 envolve entre 7 e 11 stakeholders. Mapear decisor econômico, técnico, usuário, influenciador, bloqueador e patrocinador interno antes da proposta é obrigatório para fechar contratos complexos.",
        },
      },
      {
        "@type": "Question",
        name: "O que é ancoragem em negociação?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Ancoragem é o efeito psicológico em que a primeira informação numérica apresentada influencia todas as decisões seguintes. Em vendas, significa apresentar o pacote mais alto primeiro: mesmo que o cliente negocie para baixo, fechará num patamar superior ao que fecharia se você esperasse ele falar primeiro.",
        },
      },
      {
        "@type": "Question",
        name: "Vale a pena treinar negociação com simulação de IA?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Sim. Times que treinam negociação semanalmente com simulação de IA aumentam a taxa de fechamento em média 31% em 90 dias e reduzem o desconto médio em 18%. Negociação é habilidade muscular: só evolui com repetição em ambiente controlado, exatamente como pilotos no simulador.",
        },
      },
    ],
  };

  const relatedPosts = [
    {
      title: "Social Selling no LinkedIn em 2026: Como Gerar Pipeline Sem Cold Call",
      readTime: "24 min",
      link: "/posts/social-selling-linkedin-2026",
      image: postCoverSocialSelling,
    },
    {
      title: "Pré-Vendas em 2026: Como Estruturar um Time SDR/BDR Que Gera Pipeline Previsível",
      readTime: "22 min",
      link: "/posts/pre-vendas-sdr-bdr-2026",
      image: postCoverPreVendas,
    },
    {
      title: "13 Estratégias de Vendas Para Dominar Qualquer Negociação em 2026",
      readTime: "25 min",
      link: "/posts/13-estrategias-vendas-2026",
      image: postCoverEstrategias,
    },
  ];

  return (
    <>
      <SEOHead
        title="Negociação B2B em 2026: Como Fechar Grandes Contratos"
        description="Guia completo de negociação B2B em 2026 em 13 passos: BATNA, ZOPA, ancoragem, concessões, stakeholders, contratos, walk-away power e treino com IA."
        keywords="negociação b2b, técnicas de negociação, batna, zopa, ancoragem, fechamento de vendas, negociação comercial, contratos b2b, walk-away power, negociação 2026"
        canonicalUrl="https://bianchimbusiness.com.br/posts/negociacao-b2b-2026"
        ogType="article"
        articlePublishedTime="2026-05-05"
        articleAuthor="Junior Bianchim"
      />

      <div className="min-h-screen bg-background">
        <Header />

        <main>
          <article>
            {/* Hero */}
            <section className="relative">
              <div className="w-full h-[50vh] sm:h-[60vh] relative overflow-hidden">
                <img
                  src={postCoverNegociacao}
                  alt="Junior Bianchim em mentoria de negociação B2B com líderes comerciais"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
              </div>
              <div className="container mx-auto px-4 relative -mt-32 sm:-mt-40 z-10">
                <div className="max-w-4xl mx-auto">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary text-sm font-semibold mb-4">
                    Negociação Avançada
                  </span>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight mb-4 text-foreground">
                    Negociação B2B em 2026:{" "}
                    <span className="text-gradient-brand">
                      Como Conduzir Reuniões Que Fecham Grandes Contratos
                    </span>
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mt-6">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span>Junior Bianchim</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>23 min de leitura</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4" />
                      <span>05 Mai 2026</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Introduction */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <div className="prose prose-lg max-w-none text-muted-foreground space-y-6">
                    <p className="text-xl leading-relaxed">
                      <strong className="text-foreground">Qual é o seu maior negócio do ano travado em desconto há 30 dias?</strong> Em 2026, com compradores B2B treinados em frameworks de procurement e IA ajudando a preparar contrapropostas, negociação virou habilidade técnica — não improviso. Quem não domina BATNA, ZOPA, ancoragem e concessão estratégica perde dinheiro toda vez que entra numa reunião final.
                    </p>
                    <p>
                      O problema é que a maioria dos vendedores aprendeu negociação no campo, errando com cliente real, queimando margem e perdendo deals que estavam fechados. <strong className="text-foreground">Negociar não é dom — é método</strong>. E método se treina em simulação, igual piloto no simulador antes do primeiro voo de verdade.
                    </p>
                    <p>
                      Este guia é o playbook que aplico em times de empresas como Citróleo Group, Latina Eletrodomésticos, Mapfre Seguros, Zentai Benefícios e Triplos Tecnologia. <strong className="text-foreground">13 passos práticos para conduzir reuniões finais com autoridade, preservar margem e fechar grandes contratos sem desespero.</strong>
                    </p>
                    <Card className="p-6 border-primary/20 bg-primary/5 my-8">
                      <div className="flex items-start gap-4">
                        <AlertTriangle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <p className="font-bold text-foreground mb-2">Antes de continuar, responda honestamente:</p>
                          <p className="text-muted-foreground">
                            Na sua última reunião de fechamento, <strong className="text-foreground">você conduziu a negociação com método ou foi conduzido pelo comprador?</strong> Essa resposta diz tudo sobre por que sua taxa de desconto está onde está.
                          </p>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </section>

            {/* Image - reuniao */}
            <section className="py-6">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src={postReuniao}
                      alt="Sessão de mentoria presencial sobre condução estratégica de negociação comercial B2B"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* The 13 Steps */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-foreground">
                    Os 13 Passos Para Dominar Negociação B2B em 2026
                  </h2>
                  <p className="text-muted-foreground mb-10">
                    Cada passo é um diagnóstico + ação + checklist. Implemente em sequência ou ataque primeiro o que mais dói no seu pipeline atual.
                  </p>

                  <div className="space-y-12">
                    {checklist.map((item, index) => {
                      const Icon = item.icon;
                      return (
                        <div key={index} id={`passo-${item.number}`}>
                          <Card className="p-6 sm:p-8 border-border hover:border-primary/30 transition-all">
                            <div className="flex items-center gap-4 mb-6">
                              <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                                <span className="text-primary font-extrabold text-xl">{item.number}</span>
                              </div>
                              <div className="flex-1">
                                <h3 className="text-xl sm:text-2xl font-bold text-foreground">{item.title}</h3>
                              </div>
                              <Icon className="w-6 h-6 text-primary flex-shrink-0 hidden sm:block" />
                            </div>

                            <div className="bg-destructive/5 border border-destructive/20 rounded-xl p-4 mb-6">
                              <p className="text-sm font-semibold text-destructive flex items-center gap-2 mb-1">
                                <AlertTriangle className="w-4 h-4" />
                                Diagnóstico
                              </p>
                              <p className="text-muted-foreground">{item.problem}</p>
                            </div>

                            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mb-6">
                              <p className="text-sm font-semibold text-primary flex items-center gap-2 mb-1">
                                <Zap className="w-4 h-4" />
                                Ação Imediata
                              </p>
                              <p className="text-muted-foreground">{item.action}</p>
                            </div>

                            <div className="space-y-3 mb-6">
                              <p className="text-sm font-semibold text-foreground flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-primary" />
                                Checklist de Implementação
                              </p>
                              {item.checkItems.map((check, i) => (
                                <div key={i} className="flex items-start gap-3 pl-2">
                                  <div className="w-5 h-5 rounded border-2 border-primary/40 flex-shrink-0 mt-0.5" />
                                  <span className="text-sm text-muted-foreground">{check}</span>
                                </div>
                              ))}
                            </div>

                            {item.story && (
                              <div className="bg-muted/30 rounded-xl p-5 border-l-4 border-primary">
                                <p className="text-sm font-semibold text-foreground flex items-center gap-2 mb-3">
                                  <Star className="w-4 h-4 text-primary" />
                                  Case Real
                                </p>
                                <p className="text-sm text-muted-foreground leading-relaxed italic">
                                  {item.story}
                                </p>
                              </div>
                            )}
                          </Card>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </section>

            {/* Image - handshake */}
            <section className="py-6">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src={postHandshake}
                      alt="Treinamento prático com equipe comercial aplicando técnicas de fechamento de contratos B2B"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Summary */}
            <section className="py-10 sm:py-16 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-foreground">
                    Resumo: O Mapa Completo da Negociação B2B Profissional
                  </h2>
                  <Card className="p-6 sm:p-8 border-primary/20">
                    <div className="space-y-4">
                      {checklist.map((item, index) => (
                        <a
                          key={index}
                          href={`#passo-${item.number}`}
                          className="flex items-center gap-4 p-3 rounded-lg hover:bg-primary/5 transition-colors group cursor-pointer"
                        >
                          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-primary font-bold text-sm">{item.number}</span>
                          </div>
                          <span className="text-foreground font-medium group-hover:text-primary transition-colors flex-1">
                            {item.title}
                          </span>
                          <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                        </a>
                      ))}
                    </div>
                  </Card>
                </div>
              </div>
            </section>

            {/* Conclusion */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-foreground">
                    Negociação Não é Improviso — É Método Treinado em Repetição
                  </h2>
                  <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
                    <p>
                      O vendedor que entra na reunião final achando que vai "ler o cliente" perde para quem entrou com BATNA mapeada, ancoragem definida, contrapartidas listadas e walk-away power preservado. Em 2026, o comprador B2B está mais técnico, mais informado e mais protegido por procurement. <strong className="text-foreground">Quem ainda negocia no instinto vai sempre perder margem.</strong>
                    </p>
                    <p>
                      Valor antes de preço. BATNA antes da mesa. Ancoragem alta. Concessão com contrapartida. Silêncio estratégico. Stakeholders mapeados. Coragem para dizer não. Reunião conduzida com agenda. Urgência real. Termos blindados. Prova social no fechamento. Walk-away power preservado. Treino semanal em simulação. <strong className="text-foreground">Quando esses 13 elementos rodam juntos, o time deixa de torcer pelo fechamento — passa a conduzir.</strong>
                    </p>
                    <p>
                      O contrato grande que você está perseguindo vai fechar com método ou vai morrer em desconto. A diferença está na preparação anterior à reunião, não no improviso durante ela.
                    </p>
                    <p>
                      <strong className="text-foreground">Comece pelo passo 01 hoje. Implemente um passo por semana. Em 90 dias, sua taxa de desconto cai, seu ticket médio sobe e seu time para de fugir das reuniões finais — passa a desejá-las.</strong>
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Related Posts */}
            <section className="py-10 sm:py-16 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl font-bold mb-8 text-foreground">Leitura Recomendada</h2>
                  <div className="grid sm:grid-cols-3 gap-6">
                    {relatedPosts.map((post, index) => (
                      <Link key={index} to={post.link} className="block group">
                        <Card className="overflow-hidden border-border hover:border-primary/30 transition-all h-full">
                          <div className="w-full h-36 overflow-hidden">
                            <img
                              src={post.image}
                              alt={`Capa do artigo ${post.title} - conteúdo complementar sobre vendas e negociação`}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                          <div className="p-4">
                            <h3 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors line-clamp-2">
                              {post.title}
                            </h3>
                            <span className="text-xs text-muted-foreground mt-2 block">{post.readTime} de leitura</span>
                          </div>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Author */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <Card className="p-6 sm:p-8 border-primary/20 bg-gradient-to-br from-card to-primary/5">
                    <div className="flex flex-col sm:flex-row items-center gap-6">
                      <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-primary/30">
                        <img
                          src={juniorBianchim}
                          alt="Junior Bianchim, especialista em treinamento de equipes comerciais e negociação B2B"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="text-center sm:text-left">
                        <h3 className="text-xl font-bold text-foreground mb-1">Junior Bianchim</h3>
                        <p className="text-primary font-medium text-sm mb-3">Especialista em Treinamento de Equipes Comerciais</p>
                        <p className="text-muted-foreground text-sm">
                          Com mais de 20 anos de experiência em vendas B2B e B2C, Junior já estruturou operações comerciais em empresas como Citróleo Group, Latina Eletrodomésticos, Mapfre Seguros e Zentai Benefícios. Fundador do Vendas Trainer, plataforma de IA para treinamento de SDR, BDR e closers.
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </section>

            {/* CTA */}
            <section className="py-16 sm:py-20 bg-muted/30">
              <div className="container mx-auto px-4">
                <Card className="p-8 sm:p-12 bg-gradient-to-br from-card via-primary/5 to-accent/10 border-primary/30 text-center max-w-4xl mx-auto">
                  <div className="relative z-10">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">
                      <span className="text-gradient-brand">Treine Negociação B2B Com IA</span>
                    </h2>
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                      No Vendas Trainer, sua equipe pratica negociação, objeções e fechamento em simulações reais com inteligência artificial.
                      Feedback instantâneo, gamificação e métricas claras.
                      <strong className="text-foreground"> Comece grátis hoje mesmo.</strong>
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Button
                        size="lg"
                        onClick={() => navigate("/auth?mode=signup")}
                        className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow text-lg px-8 py-6 font-semibold"
                      >
                        Criar Conta Grátis
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                      <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-primary/50 hover:bg-primary/10" asChild>
                        <a href="https://wa.me/5516981960018" target="_blank" rel="noopener noreferrer">
                          Agendar Demonstração
                        </a>
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            </section>
          </article>

          <PageNavigation />
        </main>

        <Footer />
      </div>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
    </>
  );
};

export default NegociacaoB2B2026;
